import React, { useState, useEffect, useMemo } from 'react';
import { X, Truck, Calculator, CreditCard, AlertTriangle, CheckCircle, MapPin, MessageSquare, ChevronDown, Warehouse, Edit, Save, CalendarDays } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

const RETURN_ADDRESS = {
  company: 'Phocea Rent (Phocealight)',
  street: '65 Avenue Jacqueline Auriol',
  complement: 'Bâtiment 6',
  zip: '13700',
  city: 'Marignane',
  country: 'France'
};

const ReturnRequestModal = ({ isOpen, onClose, reservation, onSuccess }) => {
  const [step, setStep] = useState(1); // 1: Form, 2: Address confirmation, 3: Payment
  const [returnMethod, setReturnMethod] = useState(''); // 'carrier' | 'dropoff'
  const [departments, setDepartments] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [calculatingFee, setCalculatingFee] = useState(false);
  
  // Form data
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedReason, setSelectedReason] = useState('');
  const [message, setMessage] = useState('');
  const [returnFee, setReturnFee] = useState(null);
  const [feeDetails, setFeeDetails] = useState(null);
  
  // Client address
  const [editingAddress, setEditingAddress] = useState(false);
  const [addressConfirmed, setAddressConfirmed] = useState(false);
  const [pickupDate, setPickupDate] = useState('');
  const [clientAddress, setClientAddress] = useState({
    street: '',
    complement: '',
    zip: '',
    city: '',
    phone: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchDepartments();
      fetchReasons();
      setStep(1);
      setReturnMethod('');
      setSelectedDepartment('');
      setSelectedReason('');
      setMessage('');
      setReturnFee(null);
      setFeeDetails(null);
      setEditingAddress(false);
      setAddressConfirmed(false);
      setPickupDate('');
      // Pre-fill client address from reservation
      const customer = reservation?.customer || {};
      setClientAddress({
        street: customer.address || '',
        complement: customer.address_complement || '',
        zip: customer.postal_code || customer.zip || '',
        city: customer.city || '',
        phone: customer.phone || ''
      });
    }
  }, [isOpen, reservation]);

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/returns/departments`);
      setDepartments(response.data);
    } catch (error) {
      console.error('Failed to fetch departments:', error);
    }
  };

  const fetchReasons = async () => {
    try {
      const response = await axios.get(`${API}/returns/reasons`);
      setReasons(response.data);
    } catch (error) {
      setReasons(['Fin de projet', 'Changement de besoin', 'Contraintes budgétaires', 'Problème technique', 'Autre']);
    }
  };

  const calculateReturnFee = async () => {
    if (!selectedDepartment || !reservation) return;
    setCalculatingFee(true);
    try {
      const packId = reservation.pack_id || 'pack_standard';
      const response = await axios.post(`${API}/returns/calculate-fee`, {
        pack_id: packId,
        department: selectedDepartment,
        pack_name: reservation.pack_name || null,
        pack_surface: reservation.pack_surface || null
      });
      setReturnFee(response.data.return_fee);
      setFeeDetails(response.data);
    } catch (error) {
      toast.error('Erreur lors du calcul des frais');
    } finally {
      setCalculatingFee(false);
    }
  };

  useEffect(() => {
    if (selectedDepartment) calculateReturnFee();
  }, [selectedDepartment]);

  // Auto-open the address editor when reaching step 2 if the address is incomplete,
  // so the client is never blocked by a missing/empty pickup address.
  useEffect(() => {
    if (step === 2 && (!clientAddress.street || !clientAddress.zip || !clientAddress.city)) {
      setEditingAddress(true);
    }
  }, [step]);

  // Generate available pickup dates: 48h min to 7 business days max
  const availablePickupDates = useMemo(() => {
    const dates = [];
    const now = new Date();
    const minDate = new Date(now);
    minDate.setHours(minDate.getHours() + 48);

    let businessDaysCount = 0;
    const cursor = new Date(minDate);
    while (businessDaysCount < 7) {
      const day = cursor.getDay();
      if (day !== 0 && day !== 6) { // Exclude weekends
        dates.push(new Date(cursor));
        businessDaysCount++;
      }
      cursor.setDate(cursor.getDate() + 1);
    }
    return dates;
  }, []);

  const handleSubmit = () => {
    if (!returnMethod) {
      toast.error('Veuillez choisir un mode de retour');
      return;
    }
    if (!selectedReason) {
      toast.error('Veuillez indiquer le motif de la résiliation');
      return;
    }
    if (returnMethod === 'dropoff') {
      setStep(3); // Dépôt direct : pas d'adresse d'enlèvement ni de paiement
      return;
    }
    if (!selectedDepartment || !returnFee) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    if (!pickupDate) {
      toast.error('Veuillez sélectionner une date d\'enlèvement');
      return;
    }
    setStep(2); // Go to address confirmation
  };

  const handleSelfDropoff = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/returns/create-self-dropoff`, {
        reservation_id: reservation.id,
        reason: selectedReason,
        message: message,
        effective_date: getEffectiveDateISO(),
      }, { withCredentials: true });
      toast.success('Demande de résiliation enregistrée. Vous pouvez déposer le matériel au dépôt.');
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('Self drop-off return error:', error?.response?.data || error);
      toast.error(error?.response?.data?.detail || 'Erreur lors de l\'enregistrement de la résiliation. Merci de réessayer ou de contacter le support.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddressConfirmed = () => {
    if (!clientAddress.street || !clientAddress.zip || !clientAddress.city) {
      toast.error('Veuillez compléter votre adresse d\'enlèvement');
      return;
    }
    setStep(3); // Go to payment
  };

  const handlePayment = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/returns/create-payment`, {
        reservation_id: reservation.id,
        return_fee: returnFee,
        department: selectedDepartment,
        reason: selectedReason,
        message: message,
        origin_url: window.location.origin,
        effective_date: getEffectiveDateISO(),
        contract_date: reservation?.contract_accepted_at || reservation?.created_at,
        pickup_address: clientAddress,
        pickup_date: pickupDate
      }, { withCredentials: true });
      
      if (response.data.session_id) {
        localStorage.setItem('return_session_id', response.data.session_id);
      }
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Return payment error:', error?.response?.data || error);
      toast.error(error?.response?.data?.detail || 'Erreur lors de la création du paiement. Merci de réessayer ou de contacter le support.');
    } finally {
      setLoading(false);
    }
  };

  const getZoneName = (zone) => {
    const zoneNames = {
      'paca': 'PACA (proche)',
      'lyon': 'Lyon / Auvergne-Rhône-Alpes',
      'paris': 'Paris / Île-de-France',
      'standard': 'France métropolitaine',
      'remote': 'Zone éloignée'
    };
    return zoneNames[zone] || zone;
  };

  // Calcule la prochaine date anniversaire du contrat (date d'effet de la résiliation).
  // Robuste : si la date du contrat est absente ou non parseable (anciennes données,
  // format inattendu), on retombe sur la date du jour au lieu de produire une "Date invalide"
  // qui bloquerait la résiliation (toISOString() lève une exception sur une date invalide).
  const computeEffectiveDate = () => {
    const now = new Date();
    let contractDate = reservation?.contract_accepted_at
      ? new Date(reservation.contract_accepted_at)
      : reservation?.created_at ? new Date(reservation.created_at) : now;
    if (isNaN(contractDate.getTime())) contractDate = now;
    const anniversaryDay = contractDate.getDate();
    let nextAnniversary;
    if (now.getDate() < anniversaryDay) {
      nextAnniversary = new Date(now.getFullYear(), now.getMonth() + 1, anniversaryDay);
    } else {
      nextAnniversary = new Date(now.getFullYear(), now.getMonth() + 2, anniversaryDay);
    }
    if (nextAnniversary.getDate() !== anniversaryDay) {
      nextAnniversary = new Date(nextAnniversary.getFullYear(), nextAnniversary.getMonth() + 1, 0);
    }
    if (isNaN(nextAnniversary.getTime())) {
      nextAnniversary = new Date(now.getFullYear(), now.getMonth() + 1, now.getDate());
    }
    return nextAnniversary;
  };

  const getEffectiveDate = () =>
    computeEffectiveDate().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

  // Construit la date ISO (YYYY-MM-DD) à partir des composants LOCAUX pour éviter
  // le décalage d'un jour causé par toISOString() (qui convertit en UTC) sur les
  // fuseaux UTC+1/+2 : la date envoyée à Stripe doit être celle affichée au client.
  const getEffectiveDateISO = () => {
    const d = computeEffectiveDate();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };



  if (!isOpen) return null;

  // Step indicator (2 étapes pour le dépôt direct, 3 pour le transporteur)
  const isDropoff = returnMethod === 'dropoff';
  const stepList = isDropoff ? [1, 2] : [1, 2, 3];
  const displayStep = isDropoff && step === 3 ? 2 : step; // le dépôt saute l'étape adresse
  const StepIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-6">
      {stepList.map((s) => (
        <React.Fragment key={s}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
            displayStep >= s ? 'bg-[#DFFF00] text-black' : 'bg-slate-700 text-slate-400'
          }`}>{s}</div>
          {s < stepList.length && <div className={`w-8 h-0.5 ${displayStep > s ? 'bg-[#DFFF00]' : 'bg-slate-700'}`} />}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-slate-700">
        {/* Header */}
        <div className="sticky top-0 bg-slate-900 border-b border-slate-700 p-6 flex items-center justify-between z-10">
          <div>
            <h2 className="text-xl font-bold text-white">Demande de résiliation</h2>
            <p className="text-slate-400 text-sm mt-1">{reservation?.pack_name} - {reservation?.pack_surface}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors" data-testid="close-return-modal">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          <StepIndicator />

          {/* === STEP 1: Form === */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-300 font-medium">Non-remboursement du mois en cours</p>
                  <p className="text-red-300/80 text-sm mt-1">
                    En demandant la résiliation, <strong>le mois en cours ne sera pas remboursé</strong>. 
                    Votre accès au matériel reste actif jusqu'à la date d'effet de la résiliation.
                  </p>
                </div>
              </div>

              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-amber-200 font-medium">Date d'effet</p>
                  <p className="text-amber-200/80 text-sm mt-1">
                    La résiliation prendra effet au <strong>{getEffectiveDate()}</strong> (date anniversaire de votre contrat).
                  </p>
                </div>
              </div>

              {/* Return method selection */}
              <div>
                <label className="block text-white font-medium mb-2">
                  <Truck className="w-4 h-4 inline mr-2" />Comment souhaitez-vous retourner le matériel ? *
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setReturnMethod('carrier')}
                    className={`text-left p-4 rounded-xl border transition-all ${returnMethod === 'carrier' ? 'bg-[#DFFF00]/15 border-[#DFFF00]' : 'bg-slate-800 border-slate-600 hover:border-slate-400'}`}
                    data-testid="return-method-carrier"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Truck className={`w-5 h-5 ${returnMethod === 'carrier' ? 'text-[#DFFF00]' : 'text-slate-400'}`} />
                      <span className="text-white font-semibold text-sm">Enlèvement par transporteur</span>
                    </div>
                    <p className="text-xs text-slate-400">Le transporteur récupère le matériel chez vous. <strong className="text-slate-300">Frais de retour à payer.</strong></p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setReturnMethod('dropoff')}
                    className={`text-left p-4 rounded-xl border transition-all ${returnMethod === 'dropoff' ? 'bg-[#DFFF00]/15 border-[#DFFF00]' : 'bg-slate-800 border-slate-600 hover:border-slate-400'}`}
                    data-testid="return-method-dropoff"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Warehouse className={`w-5 h-5 ${returnMethod === 'dropoff' ? 'text-[#DFFF00]' : 'text-slate-400'}`} />
                      <span className="text-white font-semibold text-sm">Je dépose au dépôt</span>
                    </div>
                    <p className="text-xs text-slate-400">Vous ramenez le matériel vous-même au dépôt. <strong className="text-green-400">Aucun frais de retour.</strong></p>
                  </button>
                </div>
              </div>

              {/* Reason */}
              <div>
                <label className="block text-white font-medium mb-2">
                  <MessageSquare className="w-4 h-4 inline mr-2" />Motif de la résiliation *
                </label>
                <div className="relative">
                  <select value={selectedReason} onChange={(e) => setSelectedReason(e.target.value)}
                    className="w-full bg-slate-800 border border-slate-600 rounded-xl px-4 py-3 text-white appearance-none cursor-pointer focus:border-[#DFFF00] focus:outline-none"
                    data-testid="return-reason-select">
                    <option value="">Sélectionnez un motif</option>
                    {reasons.map((reason) => <option key={reason} value={reason}>{reason}</option>)}
                  </select>
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-white font-medium mb-2">Message (optionnel)</label>
                <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Dites-nous en plus sur votre décision..." rows={3}
                  className="w-full bg-slate-800 border border-slate-600 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:border-[#DFFF00] focus:outline-none resize-none"
                  data-testid="return-message-input" />
              </div>

              {/* Carrier-only fields (department, pickup date, fees, addresses) */}
              {returnMethod === 'carrier' && (
              <>
              {/* Department */}
              <div>
                <label className="block text-white font-medium mb-2">
                  <MapPin className="w-4 h-4 inline mr-2" />Département d'expédition *
                </label>
                <div className="relative">
                  <select value={selectedDepartment} onChange={(e) => setSelectedDepartment(e.target.value)}
                    className="w-full bg-slate-800 border border-slate-600 rounded-xl px-4 py-3 text-white appearance-none cursor-pointer focus:border-[#DFFF00] focus:outline-none"
                    data-testid="department-select">
                    <option value="">Sélectionnez votre département</option>
                    {departments.map((dept) => <option key={dept.code} value={dept.code}>{dept.code} - {dept.name}</option>)}
                  </select>
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                </div>
              </div>

              {/* Pickup date selector */}
              <div>
                <label className="block text-white font-medium mb-2">
                  <CalendarDays className="w-4 h-4 inline mr-2" />Date d'enlèvement souhaitée *
                </label>
                <p className="text-slate-500 text-xs mb-3">Créneaux disponibles : entre 48h et 7 jours ouvrés après votre demande</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {availablePickupDates.map((date) => {
                    const iso = date.toISOString().split('T')[0];
                    const isSelected = pickupDate === iso;
                    const dayName = date.toLocaleDateString('fr-FR', { weekday: 'short' });
                    const dayNum = date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
                    return (
                      <button
                        key={iso}
                        type="button"
                        onClick={() => setPickupDate(iso)}
                        className={`p-3 rounded-xl border text-center transition-all ${
                          isSelected
                            ? 'bg-[#DFFF00]/15 border-[#DFFF00] text-[#DFFF00]'
                            : 'bg-slate-800 border-slate-600 text-white hover:border-slate-400'
                        }`}
                        data-testid={`pickup-date-${iso}`}
                      >
                        <span className="block text-xs capitalize text-slate-400">{dayName}</span>
                        <span className="block text-sm font-semibold">{dayNum}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Fee Calculator */}
              <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
                <div className="flex items-center gap-2 mb-4">
                  <Calculator className="w-5 h-5 text-[#DFFF00]" />
                  <h3 className="text-white font-semibold">Simulateur de frais de retour</h3>
                </div>
                {calculatingFee ? (
                  <div className="flex items-center justify-center py-6">
                    <div className="animate-spin w-6 h-6 border-2 border-[#DFFF00] border-t-transparent rounded-full" />
                    <span className="ml-3 text-slate-400">Calcul en cours...</span>
                  </div>
                ) : feeDetails ? (
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Zone tarifaire :</span>
                      <span className="text-white">{getZoneName(feeDetails.zone)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Surface :</span>
                      <span className="text-white">{feeDetails.surface_m2} m²</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Poids estimé :</span>
                      <span className="text-white">{feeDetails.weight_kg} kg</span>
                    </div>
                    <div className="border-t border-slate-600 pt-3 mt-3">
                      <div className="flex justify-between items-center">
                        <span className="text-white font-medium">Frais de retour :</span>
                        <span className="text-2xl font-bold text-[#DFFF00]">{returnFee?.toFixed(2)}€</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">TTC - Transport vers Vitrolles (13)</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6 text-slate-500">
                    <Truck className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p>Sélectionnez votre département pour calculer les frais</p>
                  </div>
                )}
              </div>

              {/* Client pickup address + Return address */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <Truck className="w-4 h-4 text-[#DFFF00]" />
                      <span className="text-[#DFFF00] font-medium text-sm">Adresse de départ (enlèvement)</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setEditingAddress(true); setStep(2); }}
                      className="text-[#DFFF00] hover:text-[#c8e600] text-xs flex items-center gap-1 flex-shrink-0"
                      data-testid="edit-address-step1-btn"
                    >
                      <Edit className="w-3 h-3" /> Modifier
                    </button>
                  </div>
                  <p className="text-white text-sm leading-relaxed">
                    {reservation?.customer?.firstName} {reservation?.customer?.lastName}<br />
                    {clientAddress.street || 'Non renseignée'}<br />
                    {clientAddress.complement && <>{clientAddress.complement}<br /></>}
                    {clientAddress.zip} {clientAddress.city}
                    {clientAddress.phone && <><br /><span className="text-slate-400">Tel: {clientAddress.phone}</span></>}
                  </p>
                  <label
                    className={`flex items-center gap-2 mt-3 p-2 rounded-lg border cursor-pointer select-none transition-colors ${addressConfirmed ? 'border-transparent' : 'border-amber-400/60 bg-amber-400/10 animate-pulse'}`}
                    data-testid="confirm-address-checkbox"
                  >
                    <input
                      type="checkbox"
                      checked={addressConfirmed}
                      onChange={(e) => setAddressConfirmed(e.target.checked)}
                      className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-[#DFFF00] focus:ring-[#DFFF00] accent-[#DFFF00] cursor-pointer"
                    />
                    <span className={`text-xs ${addressConfirmed ? 'text-slate-300' : 'text-amber-300 font-medium'}`}>Je confirme que cette adresse est correcte</span>
                  </label>
                </div>
                <div className="bg-blue-500/10 rounded-xl p-4 border border-blue-500/30">
                  <div className="flex items-center gap-2 mb-2">
                    <Warehouse className="w-4 h-4 text-blue-400" />
                    <span className="text-blue-300 font-medium text-sm">Adresse de retour (destination)</span>
                  </div>
                  <p className="text-white text-sm leading-relaxed">
                    {RETURN_ADDRESS.company}<br />
                    {RETURN_ADDRESS.street}<br />
                    {RETURN_ADDRESS.complement}<br />
                    {RETURN_ADDRESS.zip} {RETURN_ADDRESS.city}
                  </p>
                </div>
              </div>
              </>
              )}

              {/* Drop-off info box */}
              {returnMethod === 'dropoff' && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <Warehouse className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="text-green-300 font-medium">Dépôt au dépôt — aucun frais de retour</p>
                      <p className="text-green-300/80 mt-1">Vous ramenez le matériel vous-même à notre dépôt. Merci de nous contacter pour convenir d'un créneau.</p>
                      <p className="text-white mt-3 leading-relaxed">
                        <strong>{RETURN_ADDRESS.company}</strong><br />
                        {RETURN_ADDRESS.street}<br />
                        {RETURN_ADDRESS.complement}<br />
                        {RETURN_ADDRESS.zip} {RETURN_ADDRESS.city}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {(() => {
                const missing = [];
                if (!returnMethod) missing.push('le mode de retour');
                if (!selectedReason) missing.push('le motif de la résiliation');
                if (returnMethod === 'carrier') {
                  if (!selectedDepartment) missing.push('le département d\'expédition');
                  if (!returnFee) missing.push('le calcul des frais de retour');
                  if (!pickupDate) missing.push('une date d\'enlèvement');
                  if (!addressConfirmed) missing.push('la confirmation de l\'adresse d\'enlèvement');
                }
                if (missing.length === 0) return null;
                return (
                  <div className="flex items-start gap-2 bg-amber-400/10 border border-amber-400/30 rounded-lg p-3" data-testid="return-missing-fields">
                    <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-200">
                      Pour continuer, veuillez renseigner : {missing.join(', ')}.
                    </p>
                  </div>
                );
              })()}

              <Button onClick={handleSubmit}
                disabled={!returnMethod || !selectedReason || (returnMethod === 'carrier' && (!selectedDepartment || !returnFee || !addressConfirmed || !pickupDate))}
                className="w-full bg-[#DFFF00] hover:bg-[#c8e600] text-black font-semibold py-4 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="continue-return-btn">
                Continuer
              </Button>
            </div>
          )}

          {/* === STEP 2: Address Confirmation === */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 flex items-start gap-3">
                <MapPin className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-blue-300 font-medium">Confirmez votre adresse d'enlèvement</p>
                  <p className="text-blue-300/80 text-sm mt-1">
                    C'est à cette adresse que le transporteur viendra récupérer le matériel.
                  </p>
                </div>
              </div>

              {/* Client pickup address form */}
              <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-semibold flex items-center gap-2">
                    <Truck className="w-5 h-5 text-[#DFFF00]" />
                    Adresse d'enlèvement
                  </h3>
                  {!editingAddress && (
                    <button onClick={() => setEditingAddress(true)} className="text-[#DFFF00] hover:text-[#c8e600] text-sm flex items-center gap-1" data-testid="edit-address-btn">
                      <Edit className="w-3.5 h-3.5" /> Modifier
                    </button>
                  )}
                </div>

                {editingAddress ? (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-slate-400 text-xs">Adresse *</Label>
                      <Input value={clientAddress.street} onChange={(e) => setClientAddress(a => ({ ...a, street: e.target.value }))}
                        placeholder="Numéro et nom de rue" className="mt-1 bg-slate-700 border-slate-600 text-white" data-testid="pickup-street-input" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Complément d'adresse</Label>
                      <Input value={clientAddress.complement} onChange={(e) => setClientAddress(a => ({ ...a, complement: e.target.value }))}
                        placeholder="Bâtiment, étage..." className="mt-1 bg-slate-700 border-slate-600 text-white" data-testid="pickup-complement-input" />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-slate-400 text-xs">Code postal *</Label>
                        <Input value={clientAddress.zip} onChange={(e) => setClientAddress(a => ({ ...a, zip: e.target.value }))}
                          placeholder="13000" className="mt-1 bg-slate-700 border-slate-600 text-white" data-testid="pickup-zip-input" />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-slate-400 text-xs">Ville *</Label>
                        <Input value={clientAddress.city} onChange={(e) => setClientAddress(a => ({ ...a, city: e.target.value }))}
                          placeholder="Marseille" className="mt-1 bg-slate-700 border-slate-600 text-white" data-testid="pickup-city-input" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Téléphone (pour le transporteur)</Label>
                      <Input value={clientAddress.phone} onChange={(e) => setClientAddress(a => ({ ...a, phone: e.target.value }))}
                        placeholder="06 XX XX XX XX" className="mt-1 bg-slate-700 border-slate-600 text-white" data-testid="pickup-phone-input" />
                    </div>
                    <Button onClick={() => setEditingAddress(false)} size="sm" className="bg-slate-600 hover:bg-slate-500 text-white">
                      <Save className="w-3.5 h-3.5 mr-1.5" /> Enregistrer
                    </Button>
                  </div>
                ) : (
                  <div className="text-sm text-white leading-relaxed" data-testid="pickup-address-display">
                    {clientAddress.street ? (
                      <>
                        <p className="font-medium">{reservation?.customer?.firstName} {reservation?.customer?.lastName}</p>
                        <p>{clientAddress.street}</p>
                        {clientAddress.complement && <p>{clientAddress.complement}</p>}
                        <p>{clientAddress.zip} {clientAddress.city}</p>
                        {clientAddress.phone && <p className="text-slate-400 mt-1">Tel: {clientAddress.phone}</p>}
                      </>
                    ) : (
                      <p className="text-slate-500 italic">Aucune adresse renseignée — cliquez sur "Modifier" pour en ajouter une.</p>
                    )}
                  </div>
                )}
              </div>

              {/* Return destination address */}
              <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
                <h3 className="text-white font-semibold flex items-center gap-2 mb-3">
                  <Warehouse className="w-5 h-5 text-blue-400" />
                  Adresse de destination (retour)
                </h3>
                <div className="text-sm text-white leading-relaxed">
                  <p className="font-medium">{RETURN_ADDRESS.company}</p>
                  <p>{RETURN_ADDRESS.street}</p>
                  <p>{RETURN_ADDRESS.complement}</p>
                  <p>{RETURN_ADDRESS.zip} {RETURN_ADDRESS.city}</p>
                </div>
              </div>

              <div className="flex gap-3">
                <Button onClick={() => setStep(1)} variant="outline" className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800">
                  Retour
                </Button>
                <Button onClick={handleAddressConfirmed}
                  disabled={!clientAddress.street || !clientAddress.zip || !clientAddress.city}
                  className="flex-1 bg-[#DFFF00] hover:bg-[#c8e600] text-black font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="confirm-address-btn">
                  <CheckCircle className="w-4 h-4 mr-2" /> Confirmer l'adresse
                </Button>
              </div>
            </div>
          )}

          {/* === STEP 3: Payment === */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  Récapitulatif de votre demande
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Pack :</span>
                    <span className="text-white">{reservation?.pack_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Motif :</span>
                    <span className="text-white">{selectedReason}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Date d'effet :</span>
                    <span className="text-amber-400 font-medium">{getEffectiveDate()}</span>
                  </div>
                  {returnMethod === 'carrier' && (
                  <>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Département :</span>
                    <span className="text-white">{departments.find(d => d.code === selectedDepartment)?.name || selectedDepartment}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Enlèvement prévu :</span>
                    <span className="text-[#DFFF00] font-medium">{pickupDate ? new Date(pickupDate + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '-'}</span>
                  </div>
                  <div className="pt-3 border-t border-slate-600">
                    <p className="text-slate-400 text-xs mb-1">Enlèvement :</p>
                    <p className="text-white text-sm">{clientAddress.street}, {clientAddress.zip} {clientAddress.city}</p>
                  </div>
                  </>
                  )}
                  {returnMethod === 'dropoff' && (
                  <div className="flex justify-between">
                    <span className="text-slate-400">Mode de retour :</span>
                    <span className="text-green-400 font-medium">Dépôt au dépôt (aucun frais)</span>
                  </div>
                  )}
                  <div>
                    <p className="text-slate-400 text-xs mb-1">{returnMethod === 'dropoff' ? 'À déposer au dépôt :' : 'Retour vers :'}</p>
                    <p className="text-white text-sm">{RETURN_ADDRESS.street}, {RETURN_ADDRESS.zip} {RETURN_ADDRESS.city}</p>
                  </div>
                  {message && (
                    <div className="pt-3 border-t border-slate-600">
                      <span className="text-slate-400 text-sm">Votre message :</span>
                      <p className="text-white mt-1 text-sm italic">"{message}"</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/20">
                <p className="text-red-300 text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  <span>Rappel : <strong>le mois en cours ne sera pas remboursé</strong>.</span>
                </p>
              </div>

              {returnMethod === 'carrier' && (
              <div className="bg-gradient-to-r from-[#DFFF00]/10 to-[#c8e600]/10 rounded-xl p-5 border border-[#DFFF00]/30">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Frais de retour à payer</p>
                    <p className="text-3xl font-bold text-[#DFFF00]">{returnFee?.toFixed(2)}€</p>
                    <p className="text-xs text-slate-500 mt-1">Paiement unique par carte bancaire</p>
                  </div>
                  <CreditCard className="w-12 h-12 text-[#DFFF00]/50" />
                </div>
              </div>
              )}

              {returnMethod === 'dropoff' && (
              <div className="bg-green-500/10 rounded-xl p-5 border border-green-500/30">
                <div className="flex items-center gap-3">
                  <Warehouse className="w-10 h-10 text-green-400/70 flex-shrink-0" />
                  <div>
                    <p className="text-green-300 font-semibold">Aucun frais de retour</p>
                    <p className="text-xs text-green-300/80 mt-1">Vous ramenez le matériel vous-même au dépôt. Contactez-nous pour convenir d'un créneau de dépôt.</p>
                  </div>
                </div>
              </div>
              )}

              <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
                <h4 className="text-white font-medium mb-3">Que se passe-t-il ensuite ?</h4>
                {returnMethod === 'dropoff' ? (
                <ol className="space-y-2 text-sm text-slate-400">
                  <li className="flex items-start gap-2">
                    <span className="bg-[#DFFF00] text-black w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
                    <span>Votre demande de résiliation est enregistrée immédiatement</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-[#DFFF00] text-black w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
                    <span>Vous déposez le matériel à notre dépôt de Marignane (13)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-[#DFFF00] text-black w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
                    <span>Après contrôle, votre caution est restituée</span>
                  </li>
                </ol>
                ) : (
                <ol className="space-y-2 text-sm text-slate-400">
                  <li className="flex items-start gap-2">
                    <span className="bg-[#DFFF00] text-black w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
                    <span>Vous payez les frais de retour via Stripe</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-[#DFFF00] text-black w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
                    <span>Le transporteur récupère le matériel à votre adresse</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-[#DFFF00] text-black w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
                    <span>Livraison à notre entrepôt de Marignane (13)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-[#DFFF00] text-black w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">4</span>
                    <span>Après contrôle, votre caution est restituée</span>
                  </li>
                </ol>
                )}
              </div>

              <div className="flex gap-3">
                <Button onClick={() => setStep(returnMethod === 'dropoff' ? 1 : 2)} variant="outline" className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800">
                  Retour
                </Button>
                {returnMethod === 'dropoff' ? (
                <Button onClick={handleSelfDropoff} disabled={loading}
                  className="flex-1 bg-[#DFFF00] hover:bg-[#c8e600] text-black font-semibold" data-testid="confirm-dropoff-return-btn">
                  {loading ? (
                    <><div className="animate-spin w-4 h-4 border-2 border-black border-t-transparent rounded-full mr-2" />Chargement...</>
                  ) : (
                    <><CheckCircle className="w-4 h-4 mr-2" />Confirmer la résiliation</>
                  )}
                </Button>
                ) : (
                <Button onClick={handlePayment} disabled={loading}
                  className="flex-1 bg-[#DFFF00] hover:bg-[#c8e600] text-black font-semibold" data-testid="pay-return-fee-btn">
                  {loading ? (
                    <><div className="animate-spin w-4 h-4 border-2 border-black border-t-transparent rounded-full mr-2" />Chargement...</>
                  ) : (
                    <><CreditCard className="w-4 h-4 mr-2" />Payer {returnFee?.toFixed(2)}€</>
                  )}
                </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReturnRequestModal;
